import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BackButton } from '../common/styles';
import useIDPStoreRepository from '../../store/idpStoreRepository';
import useGridNavigation from '../../hooks/useGridNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { idpApi } from '../../services/api/idpApi';
import IDPFilters from './components/IDPFilters';
import IDPGrid from './components/IDPGrid';
import IDPForm from './components/IDPForm';
import IDPWizard from './components/IDPWizard';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  ControlsContainer,
  MainContainer,
  HeaderButton,
} from './styles';

const IndividualDevelopmentPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const [showFilters, setShowFilters] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [goalStatuses, setGoalStatuses] = useState([]);
  const gridRef = useRef(null);

  const {
    goals,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchGoalsWithReset,
    fetchGoalsLoadMore,
    setIsReturningFromDetails,
    resetState,
    resetFilters,
    setSearchTerm,
    selectedEmployee,
    setSelectedEmployee,
    selectedStatuses,
    setSelectedStatuses,
  } = useIDPStoreRepository();

  // Fetch goal statuses for the filter
  useEffect(() => {
    const loadGoalStatuses = async () => {
      try {
        const statuses = await idpApi.getGoalStatuses();
        setGoalStatuses(statuses || []);
      } catch (err) {
        console.error('Failed to load goal statuses:', err);
      }
    };
    loadGoalStatuses();
  }, []);

  const loadMoreCallback = useCallback(() => {
    return fetchGoalsLoadMore();
  }, [fetchGoalsLoadMore]);

  // Callback to clean up filters when navigating away
  const handleNavigateAway = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Use the GridNavigation hook with IDP routes
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem,
    handleCustomBack,
    shouldHideContent,
    loadMoreData,
    isNewItem,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    hasItems,
    navigateWithScrollState,
  } = useGridNavigation({
    gridRef,
    items: goals,
    columnCount: 3,
    rowHeight: 230,
    routes: {
      list: '/idp',
      detail: (id) => `/idp/${id}`,
      new: '/idp/new',
      additionalRoutes: [
        { path: '/view', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    onNavigateAway: handleNavigateAway
  });

  // First effect: Always reset the store on mount
  useEffect(() => {
    resetState();
  }, [resetState]);

  // Second effect: Load data when needed
  useEffect(() => {
    // Only run this effect when we're on the main repository view
    if (!shouldLoadData()) {
      return;
    }

    // Prevent reloading data if we already have items
    if (hasItems && !isLoadingMore && !shouldForceReload()) {
      return;
    }

    const forceReload = shouldForceReload();

    // Fetch data with reset parameter based on whether we need to force reload
    fetchGoalsWithReset(forceReload);

  }, [fetchGoalsWithReset, shouldLoadData, shouldForceReload, isReturningFromDetailPage, hasItems, isLoadingMore]);

  const handleBack = () => {
    navigate('/');
  };

  const handleSearchChange = (value) => {
    setSearchInputValue(value);
    setSearchTerm(value);
  };

  const isViewMode = location.pathname.includes('/view');

  const handleViewClick = (goal) => {
    navigateWithScrollState(goal, `/idp/${goal.id}/view`);
  };

  const handleEditClick = (goal) => {
    handleItemClick(goal);
  };

  // Render wizard for new IDP
  if (isNewItem) {
    return (
      <IDPWizard
        onBack={handleCustomBack}
        onComplete={(goalId) => {
          if (goalId) {
            navigate(`/idp/${goalId}`);
          } else {
            navigate('/idp');
          }
        }}
      />
    );
  }

  // Render form for existing IDP (edit or view)
  if (id) {
    return (
      <IDPForm
        goalId={id}
        onBack={handleBackToItems}
        readonly={isViewMode}
      />
    );
  }

  // Render list view
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Individual Development Plans</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <HeaderButton onClick={handleAddNewItem}>
            <FaPlus /> New Development Plan
          </HeaderButton>
          <HeaderButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaEyeSlash /> : <FaEye />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </div>
      </Header>
      <Content>
        <ControlsContainer show={showFilters}>
          <IDPFilters
            searchInputValue={searchInputValue}
            setSearchInputValue={handleSearchChange}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            goalStatuses={goalStatuses}
          />
        </ControlsContainer>
        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0
          }}>
            <span style={{ fontWeight: 600 }}>
              {pageInfo?.totalCount || 0}
            </span>
            {pageInfo?.totalCount === 1 ? 'development plan' : 'development plans'} found
          </div>

          <IDPGrid
            goals={goals}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={pageInfo}
            handleItemClick={handleItemClick}
            handleGridScroll={handleCombinedGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onView={handleViewClick}
            onEdit={handleEditClick}
            currentEmployeeId={user?.employee?.id}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default IndividualDevelopmentPlans;
